import { api } from 'src/api/axios';

import type { PhqStatus } from '../../api/w2';
import { httpStatus, isHttpStatus } from '../../usePublicTokenResource';
import { normalizeCategory } from '../constants/categories';

/**
 * W2 employee plan self-selection API (WP6). Coded EXACTLY to the FROZEN §4
 * contract in `data/ssplan-w7/report.md` (the backend, WP2–WP5, is built in
 * parallel on `origin/W2EmployeeRosterAdds` — do not wait for it; the e2e +
 * component specs stub these endpoints at the network boundary).
 *
 * Unlike the token PHQ flow, this is a RETURNING, normally-authenticated surface
 * — the employee arrives via normal login (`/login?next=/select-plans`), NOT an
 * employer token. Every call uses the authenticated `api` instance (JWT attached
 * by the axios interceptor); identity is resolved backend-side from the JWT
 * `userId` to the single selection-eligible Self employer (section Onboarding OR
 * Active per the frozen contract D1). There is NO group token.
 *
 *   GET  /employee/selection-context   → the resolved employer + offerings
 *   POST /employee/elections           → save-in-progress + server quote
 *   POST /employee/signature           → FINISH: lock + signature-time snapshot
 */

export type ElectionDecision = 'Elected' | 'Waived';

/** One who's-covered price point for an offered plan (§4 `coverageOptions`). */
export interface CoverageOption {
  /** `insurance_prices_coverage_enum` code (1 Individual … 4 Family). */
  coverage: number;
  /** FULL monthly premium for this coverage tier (what the employee pays). */
  price: number;
}

/** One employer-offered plan line the employee must Elect or Waive (§4). */
export interface SelectionOffering {
  planId: number;
  /** Normalized display bucket (see `constants/categories`). */
  category: string;
  shortName: string;
  coverageOptions: CoverageOption[];
}

/** A dependent parsed from `phqPayload` — seeds the who's-covered selector (§4). */
export interface SelectionDependent {
  firstName?: string;
  relationship?: string;
}

/** A previously saved-but-not-signed per-line decision (§4 `elections`). */
export interface SavedElection {
  planId: number;
  decision: ElectionDecision;
  coverage?: number | null;
  certificationText?: string | null;
}

/** `GET /employee/selection-context` 200 body (§4). */
export interface SelectionContext {
  employeeId: number;
  employerName: string;
  /** True once a signature row exists — the flow is locked (read-only → done). */
  locked: boolean;
  signedAt: string | null;
  dependents: SelectionDependent[];
  /** `employees.coverage` — the who's-covered default when no dependents help. */
  defaultCoverage: number | null;
  offerings: SelectionOffering[];
  elections: SavedElection[];
  /**
   * `employees.phqStatus` when the backend exposes it on the context (additive to
   * the frozen §4 shape). A `Waived` employee declined coverage outright in the
   * onboarding waiver (`W2WaiverModal`) and is EXCLUDED from self-selection
   * (selinv contract D2) — the page must gate to the done terminal, never the
   * flow. Absent on older backends → no gate (behavior unchanged).
   */
  phqStatus?: PhqStatus;
}

/** One line submitted to `POST /employee/elections` (§4 body). */
export interface ElectionLineInput {
  planId: number;
  decision: ElectionDecision;
  /** Required (backend 422 `coverageRequired`) when `decision === 'Elected'`. */
  coverage?: number;
  /** The employee's decline attestation text when `decision === 'Waived'`. */
  certificationText?: string;
}

/** One priced line in the server-computed quote (§4 elections/signature). */
export interface QuoteLine {
  planId: number;
  category?: string;
  decision?: ElectionDecision;
  coverage?: number | null;
  premium: number;
  shortName?: string;
}

/** The display-only quote (§4). FULL premium per elected line + total. */
export interface SelectionQuote {
  total: number;
  lines: QuoteLine[];
}

export interface SaveElectionsResult {
  lines: SavedElection[];
  quote: SelectionQuote;
}

/** `POST /employee/signature` body (§4). */
export interface SignSelectionBody {
  signerName: string;
  typedSignature: string;
  /** Signing date, ISO `yyyy-MM-dd` (today — set by the page, not typed). */
  date: string;
  authorized: true;
}

export interface SignSelectionResult {
  signedAt: string;
  /** The frozen quote snapshot (§2.5). Opaque here — kept for the confirmation. */
  snapshot?: Record<string, unknown>;
}

/**
 * A typed error for the selection endpoints. `code` carries the §4 error key
 * (`error.notActive`, `error.electionLocked`, `error.alreadySigned`, …) so the
 * UI can branch (e.g. treat a lock as "already done" rather than a hard failure).
 */
/**
 * The backend emits §4 error keys with trailing punctuation (live: 403
 * `{"errors":["error.notActive."]}`). Strip trailing dots/whitespace so exact
 * `=== 'error.…'` comparisons match both shapes. EVERY comparison against a
 * `SelectionApiError.code` must go through this helper.
 */
export function normalizeApiCode(code: string | undefined): string | undefined {
  return code?.replace(/[.\s]+$/, '');
}

export class SelectionApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  /** §4 409 `multipleEmployers` disambiguation list (Risk R6). */
  readonly candidates?: unknown[];

  constructor(
    message: string,
    opts: { status?: number; code?: string; candidates?: unknown[] } = {}
  ) {
    super(message);
    this.name = 'SelectionApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.candidates = opts.candidates;
  }
}

// --- Normalizers (allowlist; the wire is trusted only for the shapes below) ---

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // numeric(12,2) columns serialize as strings — accept both.
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function normalizeDecision(value: unknown): ElectionDecision | undefined {
  return value === 'Elected' || value === 'Waived' ? value : undefined;
}

function normalizePhqStatus(value: unknown): PhqStatus | undefined {
  return value === 'NotStarted' ||
    value === 'InProgress' ||
    value === 'Completed' ||
    value === 'Waived'
    ? value
    : undefined;
}

function normalizeCoverageOptions(value: unknown): CoverageOption[] {
  if (!Array.isArray(value)) return [];
  const out: CoverageOption[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const coverage = asNumber((raw as { coverage?: unknown }).coverage);
    const price = asNumber((raw as { price?: unknown }).price);
    if (coverage === undefined || price === undefined) continue;
    out.push({ coverage, price });
  }
  return out;
}

function normalizeOfferings(value: unknown): SelectionOffering[] {
  if (!Array.isArray(value)) return [];
  const out: SelectionOffering[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const planId = asNumber((raw as { planId?: unknown }).planId);
    const category = asString((raw as { category?: unknown }).category);
    if (planId === undefined || !category) continue;
    out.push({
      planId,
      category: normalizeCategory(category),
      shortName:
        asString((raw as { shortName?: unknown }).shortName) ??
        `Plan ${planId}`,
      coverageOptions: normalizeCoverageOptions(
        (raw as { coverageOptions?: unknown }).coverageOptions
      ),
    });
  }
  return out;
}

function normalizeDependents(value: unknown): SelectionDependent[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((d): d is Record<string, unknown> => !!d && typeof d === 'object')
    .map((d) => ({
      firstName: asString(d.firstName),
      relationship: asString(d.relationship),
    }));
}

function normalizeSavedElections(value: unknown): SavedElection[] {
  if (!Array.isArray(value)) return [];
  const out: SavedElection[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const planId = asNumber((raw as { planId?: unknown }).planId);
    const decision = normalizeDecision(
      (raw as { decision?: unknown }).decision
    );
    if (planId === undefined || !decision) continue;
    out.push({
      planId,
      decision,
      coverage: asNumber((raw as { coverage?: unknown }).coverage) ?? null,
      certificationText:
        asString((raw as { certificationText?: unknown }).certificationText) ??
        null,
    });
  }
  return out;
}

/** A finite, NON-negative number (money never goes below zero), or undefined. */
function asNonNegative(value: unknown): number | undefined {
  const n = asNumber(value);
  return n !== undefined && n >= 0 ? n : undefined;
}

/**
 * The quote is a MONEY/CONSENT boundary, not a best-effort display blob: a
 * malformed 2xx must be rejected as a load/save error, never silently coerced to
 * `$0` (finding #7) and never allowed to disagree with what the employee is about
 * to sign. Field-level checks are not enough — Review shows the LOCAL coverage
 * tier next to the SERVER premium and the SERVER total, so the decoder must
 * validate the RELATIONSHIP between what we submitted and what came back:
 *
 *   • the quote object present, with a finite, non-negative `total`;
 *   • a `lines` array where EVERY line has a valid `planId` and a finite,
 *     non-negative `premium` — never coerce a missing premium to 0;
 *   • no duplicate lines and no line for a plan we did not submit (an extra
 *     priced line silently inflates the total the employee consents to);
 *   • every ELECTED plan we submitted priced exactly once (a missing elected
 *     price is the "$0 shown at consent time" defect);
 *   • each elected line agreeing with the submitted election: `decision`, when
 *     present, is `Elected`, and the priced `coverage` is present and EQUAL to
 *     the tier we submitted. A quote that prices employee-only while Review
 *     labels the row "family" is exactly the wrong-coverage consent defect; a
 *     quote that omits the tier cannot be checked, so it is rejected too;
 *   • each waived line priced at 0 (and `decision`, when present, `Waived`);
 *   • `total` agreeing, to currency precision, with the quote's aggregate
 *     `grossTotal - contributionApplied` when the backend supplies those fields.
 *     Employer contribution can include an amount that is not allocated onto an
 *     individual line, so line premiums are only the fallback total basis for the
 *     older shape that omits both aggregate fields.
 *
 * Any violation throws `SelectionApiError` — the caller surfaces it as a save
 * error and never advances to Review/Agreement/signature on an unvalidated quote.
 */
const MALFORMED_QUOTE_MESSAGE =
  'We could not confirm your quote. Please try again.';

/** Money compared at cent precision — never trust binary float equality. */
function cents(amount: number): number {
  return Math.round(amount * 100);
}

function decodeQuoteStrict(
  value: unknown,
  submitted: readonly ElectionLineInput[]
): SelectionQuote {
  if (!value || typeof value !== 'object') {
    throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
  }
  const quote = value as Record<string, unknown>;
  const total = asNonNegative(quote.total);
  if (total === undefined) {
    throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
  }
  const rawLines = quote.lines;
  if (!Array.isArray(rawLines)) {
    throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
  }
  const submittedByPlan = new Map<number, ElectionLineInput>();
  for (const line of submitted) submittedByPlan.set(line.planId, line);

  const lines: QuoteLine[] = [];
  const seen = new Set<number>();
  let electedSum = 0;
  for (const raw of rawLines) {
    if (!raw || typeof raw !== 'object') {
      throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
    }
    const planId = asNumber((raw as { planId?: unknown }).planId);
    const premium = asNonNegative((raw as { premium?: unknown }).premium);
    // A present line MUST carry a real planId and a real, non-negative premium —
    // never coerce a missing/garbage premium to 0.
    if (planId === undefined || premium === undefined) {
      throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
    }
    // One priced line per submitted plan, and only for plans we submitted.
    if (seen.has(planId)) {
      throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
    }
    seen.add(planId);
    const election = submittedByPlan.get(planId);
    if (!election) {
      throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
    }
    const decision = normalizeDecision(
      (raw as { decision?: unknown }).decision
    );
    if (decision !== undefined && decision !== election.decision) {
      throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
    }
    const coverage = asNumber((raw as { coverage?: unknown }).coverage) ?? null;
    if (election.decision === 'Elected') {
      // The tier priced must be the tier chosen — Review labels the row from the
      // LOCAL election, so a mismatch shows one coverage at another's price.
      if (coverage === null || coverage !== election.coverage) {
        throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
      }
      electedSum += premium;
    } else if (premium !== 0) {
      // A waived line cannot cost anything.
      throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
    }
    lines.push({
      planId,
      category: asString((raw as { category?: unknown }).category),
      decision,
      coverage,
      premium,
      shortName: asString((raw as { shortName?: unknown }).shortName),
    });
  }
  // Every elected line must be priced — an elected plan absent from the quote is
  // the fail-open the finding targets (it would show $0 for a real election).
  for (const line of submitted) {
    if (line.decision === 'Elected' && !seen.has(line.planId)) {
      throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
    }
  }
  // The live quote can apply part of the employer contribution only at the
  // aggregate level. In that shape, summing per-line premiums overstates what
  // the employee owes; validate the authoritative aggregate arithmetic instead.
  // Older responses do not carry either aggregate field, so retain the strict
  // line-sum relationship for that legacy shape.
  const rawGrossTotal = quote.grossTotal;
  const rawContributionApplied = quote.contributionApplied;
  if (rawGrossTotal !== undefined || rawContributionApplied !== undefined) {
    const grossTotal = asNonNegative(rawGrossTotal);
    const contributionApplied = asNonNegative(rawContributionApplied);
    if (
      grossTotal === undefined ||
      contributionApplied === undefined ||
      contributionApplied > grossTotal ||
      cents(total) !== cents(grossTotal - contributionApplied)
    ) {
      throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
    }
  } else if (cents(total) !== cents(electedSum)) {
    throw new SelectionApiError(MALFORMED_QUOTE_MESSAGE);
  }
  return { total, lines };
}

function normalizeContext(raw: unknown): SelectionContext | null {
  if (!raw || typeof raw !== 'object') return null;
  const employeeId = asNumber((raw as { employeeId?: unknown }).employeeId);
  if (employeeId === undefined) return null;
  return {
    employeeId,
    employerName:
      asString((raw as { employerName?: unknown }).employerName) ??
      'your employer',
    locked: (raw as { locked?: unknown }).locked === true,
    signedAt: asString((raw as { signedAt?: unknown }).signedAt) ?? null,
    dependents: normalizeDependents(
      (raw as { dependents?: unknown }).dependents
    ),
    defaultCoverage:
      asNumber((raw as { defaultCoverage?: unknown }).defaultCoverage) ?? null,
    offerings: normalizeOfferings((raw as { offerings?: unknown }).offerings),
    elections: normalizeSavedElections(
      (raw as { elections?: unknown }).elections
    ),
    phqStatus: normalizePhqStatus((raw as { phqStatus?: unknown }).phqStatus),
  };
}

function errorEnvelope(err: unknown): { code?: string; message?: string } {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return {};
  const rec = data as Record<string, unknown>;
  const code =
    asString(rec.errorCode) ??
    asString(rec.error) ??
    (Array.isArray(rec.errors) ? asString(rec.errors[0]) : undefined);
  const message =
    asString(rec.message) ??
    (Array.isArray(rec.errors) && typeof rec.errors[0] === 'object'
      ? asString((rec.errors[0] as { message?: unknown }).message)
      : undefined);
  return { code, message };
}

function toSelectionError(err: unknown, fallback: string): SelectionApiError {
  const { code, message } = errorEnvelope(err);
  const data = (err as { response?: { data?: unknown } })?.response?.data as
    | Record<string, unknown>
    | undefined;
  const candidates = Array.isArray(data?.candidates)
    ? (data!.candidates as unknown[])
    : undefined;
  return new SelectionApiError(message ?? fallback, {
    status: httpStatus(err),
    code,
    candidates,
  });
}

// --- Calls --------------------------------------------------------------------

/**
 * Resolve the returning employee's single selection-eligible Self employer
 * (section Onboarding OR Active per contract D1) and its offered plan set. Throws
 * `SelectionApiError` (with the §4 `code` — keys unchanged by D1) on the gate
 * rejections (403 not-Active for a section that never opens selection / not-Self
 * / not-small, 404 not-enrolled, 409 multi-employer) so the page can render the
 * right terminal state.
 */
export async function getSelectionContext(): Promise<SelectionContext> {
  try {
    const res = await api.get('/employee/selection-context');
    const ctx = normalizeContext(res.data);
    if (!ctx) {
      throw new SelectionApiError('We could not load your plan selection.');
    }
    return ctx;
  } catch (err) {
    if (err instanceof SelectionApiError) throw err;
    throw toSelectionError(err, 'We could not load your plan selection.');
  }
}

/**
 * Save the full per-offered-line decision set (save-in-progress) and get the
 * server-computed display quote back. 422 `coverageRequired` / `planNotOffered`
 * and 409 `electionLocked` surface as `SelectionApiError` with the §4 code.
 */
export async function saveElections(
  lines: ElectionLineInput[]
): Promise<SaveElectionsResult> {
  try {
    const res = await api.post('/employee/elections', { lines });
    const data = (res.data ?? {}) as Record<string, unknown>;
    // STRICT: a malformed OR inconsistent quote in a 2xx throws here and is
    // surfaced as a save error below — the flow never reaches Review/Agreement
    // without a quote that matches the elections we just submitted.
    const quote = decodeQuoteStrict(data.quote, lines);
    return {
      lines: normalizeSavedElections(data.lines),
      quote,
    };
  } catch (err) {
    // Preserve a SelectionApiError we raised ourselves (e.g. the strict-quote
    // rejection) instead of re-wrapping it into a generic transport error.
    if (err instanceof SelectionApiError) throw err;
    throw toSelectionError(err, 'We could not save your selections.');
  }
}

/**
 * FINISH: submit the e-signature. The backend validates every offered line has a
 * decision (422 `linesIncomplete`), requires `authorized === true` (422
 * `notAuthorized`), freezes the snapshot, and locks the election (409
 * `alreadySigned` on a second call).
 */
export async function sign(
  body: SignSelectionBody
): Promise<SignSelectionResult> {
  try {
    const res = await api.post('/employee/signature', body);
    const data = (res.data ?? {}) as Record<string, unknown>;
    return {
      signedAt: asString(data.signedAt) ?? new Date().toISOString(),
      snapshot:
        data.snapshot && typeof data.snapshot === 'object'
          ? (data.snapshot as Record<string, unknown>)
          : undefined,
    };
  } catch (err) {
    throw toSelectionError(err, 'We could not record your signature.');
  }
}

/** Re-export the status helper so callers needn't reach into the PHQ module. */
export { isHttpStatus };
